import { useEffect, useState } from 'react'

/*
 * Two routes, and no router.
 *
 * `react-router` is ~18 kB gzipped. S4.1 measured this app's initial JS at
 * 62.5 kB against V1_2_SPEC.md §8's 75 kB budget, which leaves 12.5 kB for
 * everything the page actually does — so a router would be most of the
 * remaining headroom, spent on a data loader, a nested-route tree, a form
 * action layer and a scroll restorer that a two-route static page uses none of.
 *
 * What is left when those go is this: one path pattern, `popstate`, and
 * `pushState`. It is not a smaller router, it is the absence of one, and it is
 * only viable because the route set is genuinely two entries and V1_2_SPEC.md §9
 * keeps it that way.
 *
 * The one thing worth being careful about is that a real navigation still
 * happens for anything this does not recognise: an unknown path falls through to
 * the home route rather than rendering blank, and the anchors are real `href`s,
 * so a middle-click, a copied link and a page with no JavaScript all behave.
 */

/** `PC-INC-` and six uppercase alphanumerics — `references.is_well_formed`'s
 *  shape, checked here so a malformed path never becomes a request. */
const INCIDENT_PATH = /^\/incidents\/(PC-INC-[A-Z0-9]{6})\/?$/

export type Route =
  | { readonly name: 'home' }
  | { readonly name: 'incident'; readonly reference: string }

export function routeFor(pathname: string): Route {
  const match = INCIDENT_PATH.exec(pathname)
  if (match?.[1] !== undefined) return { name: 'incident', reference: match[1] }
  return { name: 'home' }
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => routeFor(window.location.pathname))

  useEffect(() => {
    const onPopState = () => {
      setRoute(routeFor(window.location.pathname))
    }
    window.addEventListener('popstate', onPopState)
    return () => {
      window.removeEventListener('popstate', onPopState)
    }
  }, [])

  useEffect(() => {
    /*
     * In-app navigation, intercepted at the document.
     *
     * A capture-phase listener rather than an `onClick` on every anchor: the
     * anchors are ordinary `<a href>` elements so that middle-click, "open in
     * new tab", and a copied address all do what a reader expects, and this
     * upgrades a plain left-click to a client-side transition without any of
     * them having to know about it.
     */
    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

      /* `instanceof Element` rather than a cast: the cast asserts something
         about every possible event target, and this only has to be true of the
         ones that are elements. */
      const target = event.target
      if (!(target instanceof Element)) return
      const anchor = target.closest('a')
      if (anchor === null) return
      if (anchor.target !== '' && anchor.target !== '_self') return
      if (anchor.hasAttribute('download')) return

      const href = anchor.getAttribute('href')
      if (href === null || href.startsWith('/') === false) return

      const url = new URL(href, window.location.origin)
      if (url.origin !== window.location.origin) return

      event.preventDefault()
      window.history.pushState(null, '', url.pathname)
      setRoute(routeFor(url.pathname))
      window.scrollTo(0, 0)
    }

    document.addEventListener('click', onClick, { capture: true })
    return () => {
      document.removeEventListener('click', onClick, { capture: true })
    }
  }, [])

  return route
}
